<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
    <button
      class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
      @click="expanded = !expanded"
    >
      <svg class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform" :class="expanded ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
      <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">Jaaranalyse</h3>
      <span v-if="expanded && isLoading" class="tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0" />
      <template v-else-if="analysis">
        <span class="tw-text-xs tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium" :class="badgeClass(analysis.summary.yearRatioToPrognosis)">
          {{ analysis.summary.yearRatioToPrognosis ? `${analysis.summary.yearRatioToPrognosis}% prognose` : `${analysis.summary.monthsWithData} mnd` }}
        </span>
      </template>
    </button>

    <div v-if="expanded" class="tw-px-4 tw-pb-4">
      <div v-if="isLoading" class="tw-text-xs tw-text-gray-400 tw-py-6 tw-text-center">
        <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-inline-block tw-mr-2" />
        Data laden...
      </div>
      <div v-else-if="!analysis" class="tw-text-xs tw-text-gray-400 tw-py-4 tw-text-center">Onvoldoende data.</div>

      <template v-else>
        <div class="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-3">
          <!-- Prognosis -->
          <div class="tw-rounded-lg tw-p-2.5" :class="blockBg(analysis.summary.yearRatioToPrognosis)">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5" :class="blockIcon(analysis.summary.yearRatioToPrognosis)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="blockText(analysis.summary.yearRatioToPrognosis)">Prognose</span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="blockText(analysis.summary.yearRatioToPrognosis)">
              {{ analysis.summary.yearRatioToPrognosis ? `${analysis.summary.yearRatioToPrognosis}%` : '—' }}
            </div>
            <div class="tw-text-xs" :class="blockSub(analysis.summary.yearRatioToPrognosis)">
              {{ analysis.summary.yearRatioToPrognosis ? 'van jaarprognose' : 'geen prognose' }}
            </div>
          </div>

          <!-- vs Neighbors -->
          <div class="tw-rounded-lg tw-p-2.5" :class="blockBg(analysis.summary.yearRatioToNeighbors)">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5" :class="blockIcon(analysis.summary.yearRatioToNeighbors)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="blockText(analysis.summary.yearRatioToNeighbors)">
                {{ label }}
              </span>
            </div>
            <div class="tw-text-lg tw-font-bold tw-font-mono" :class="blockText(analysis.summary.yearRatioToNeighbors)">
              {{ analysis.summary.yearRatioToNeighbors ? `${analysis.summary.yearRatioToNeighbors}%` : '—' }}
            </div>
            <div v-if="analysis.summary.yearRatioToNeighbors" class="tw-text-xs tw-text-gray-400">
              van burengemiddelde (Wh/kWp)
            </div>
            <div class="tw-text-xs" :class="blockSub(analysis.summary.yearRatioToNeighbors)">
              {{ analysis.summary.neighborsUsed > 0
                ? analysis.summary.yearRatioToNeighbors > 110
                  ? `bovengemiddeld vs ${analysis.summary.neighborsUsed} installaties`
                  : analysis.summary.yearRatioToNeighbors >= 90
                    ? `in lijn met ${analysis.summary.neighborsUsed} installaties`
                    : `ondergemiddeld vs ${analysis.summary.neighborsUsed} installaties`
                : 'geen vergelijking' }}
            </div>
          </div>

          <!-- Seasonal shadow -->
          <div class="tw-rounded-lg tw-p-2.5" :class="analysis.summary.seasonalShadow && analysis.summary.seasonalShadow.winterRatio < 60 ? 'tw-bg-amber-50' : 'tw-bg-green-50'">
            <div class="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
              <svg class="tw-w-3.5 tw-h-3.5" :class="analysis.summary.seasonalShadow && analysis.summary.seasonalShadow.winterRatio < 60 ? 'tw-text-amber-500' : 'tw-text-green-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide" :class="analysis.summary.seasonalShadow && analysis.summary.seasonalShadow.winterRatio < 60 ? 'tw-text-amber-700' : 'tw-text-green-700'">Seizoen</span>
            </div>
            <template v-if="analysis.summary.seasonalShadow">
              <div class="tw-text-sm tw-font-bold" :class="analysis.summary.seasonalShadow.winterRatio < 60 ? 'tw-text-amber-700' : 'tw-text-green-700'">
                Z:{{ analysis.summary.seasonalShadow.summerRatio }}% W:{{ analysis.summary.seasonalShadow.winterRatio }}%
              </div>
              <div class="tw-text-xs" :class="analysis.summary.seasonalShadow.winterRatio < 60 ? 'tw-text-amber-500' : 'tw-text-green-500'">
                {{ analysis.summary.seasonalShadow.winterRatio < 60 ? 'winterschaduw' : 'normaal seizoensverloop' }}
              </div>
            </template>
            <template v-else>
              <div class="tw-text-sm tw-font-bold tw-text-gray-500">—</div>
              <div class="tw-text-xs tw-text-gray-400">geen vergelijking</div>
            </template>
          </div>
        </div>

        <!-- Findings -->
        <div v-if="analysis.segments.length > 0" class="tw-space-y-1">
          <div v-for="(seg, idx) in analysis.segments" :key="idx" class="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-py-1">
            <svg v-if="seg.type === 'critical'" class="tw-w-3.5 tw-h-3.5 tw-text-red-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
            <svg v-else-if="seg.type === 'warning'" class="tw-w-3.5 tw-h-3.5 tw-text-amber-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
            <svg v-else-if="seg.type === 'good'" class="tw-w-3.5 tw-h-3.5 tw-text-green-500 tw-mt-0.5 tw-flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
            <span v-else class="tw-w-3.5 tw-h-3.5 tw-rounded-full tw-bg-gray-300 tw-inline-block tw-mt-0.5 tw-flex-shrink-0" />
            <span class="tw-text-gray-700 tw-leading-relaxed">{{ seg.text }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueries } from '@tanstack/vue-query'
import { format, subMonths, startOfYear } from 'date-fns'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { analyzeYear } from './yearlyAnalysis'
import { MAX_NEIGHBORS } from './constants'
import { STATUS_COLORS, statusForRatio, ratioBarColor, ratioTextColor } from './statusColors'

const props = defineProps({
  serialNumber: { type: String, required: true },
  // how much of the serial's address the main line covers: address (default, every meter there, a
  // CVZ's fake addresses too), installation or meter; neighbors always cover their own address
  scope: { type: String, default: 'address' },
  neighborMeters: { type: Array, default: () => [] },
  neighborMatchType: { type: String, default: null },
  mainWp: { type: Number, default: null },
  neighborWpMap: { type: Object, default: () => ({}) },
  expectedProduction: { type: Number, default: null },
})

const expanded = ref(false)
const label = computed(() => props.neighborMatchType === 'postcode' ? 'Postcode' : 'Buren')

const dateFrom = format(subMonths(new Date(), 12), 'yyyy-MM-dd')
const dateTill = format(new Date(), 'yyyy-MM-dd')

const { data: mainData, isLoading: isLoadingMain } = useQuery({
  queryKey: computed(() => ['yearAnalysis', props.serialNumber, props.scope]),
  queryFn: async () => {
    const params = new URLSearchParams({ serial_number: props.serialNumber, date_from: dateFrom, date_till: dateTill, scope: props.scope })
    const result = await fetchData(`${READINGS_API_BASE}/month-aggregates?${params}`)
    return result?.status === 'OK' ? result.data : []
  },
  refetchOnWindowFocus: false,
  staleTime: 10 * 60 * 1000,
  enabled: computed(() => expanded.value),
})

const neighborQueries = computed(() =>
  expanded.value
    ? props.neighborMeters.slice(0, MAX_NEIGHBORS).map(m => ({
        queryKey: ['yearAnalysis', m.serialNumber],
        queryFn: async () => {
          const params = new URLSearchParams({ serial_number: m.serialNumber, date_from: dateFrom, date_till: dateTill })
          const result = await fetchData(`${READINGS_API_BASE}/month-aggregates?${params}`)
          return result?.status === 'OK' ? result.data : []
        },
        refetchOnWindowFocus: false,
        staleTime: 10 * 60 * 1000,
      }))
    : []
)

const neighborResults = useQueries({ queries: neighborQueries })
const neighborMonthlyMap = ref({})
watch(neighborResults, (results) => {
  const map = {}
  if (!results) return
  props.neighborMeters.slice(0, MAX_NEIGHBORS).forEach((m, i) => {
    if (results[i]?.data) map[m.serialNumber] = results[i].data
  })
  neighborMonthlyMap.value = map
}, { deep: true, immediate: true })

const neighborLoading = ref(false)
watch(neighborResults, (results) => {
  if (!results || results.length === 0) { neighborLoading.value = false; return }
  neighborLoading.value = results.some(q => q?.isLoading || q?.isFetching)
}, { deep: true, immediate: true })

const isLoading = computed(() => isLoadingMain.value || neighborLoading.value)

const analysis = computed(() => {
  if (!mainData.value) return null
  return analyzeYear({
    mainMonthly: mainData.value,
    neighborMonthlyMap: neighborMonthlyMap.value,
    neighbors: props.neighborMeters.slice(0, MAX_NEIGHBORS),
    mainWp: props.mainWp,
    neighborWpMap: props.neighborWpMap,
    matchType: props.neighborMatchType,
    expectedProduction: props.expectedProduction,
  })
})

const blockBg = (r) => STATUS_COLORS[statusForRatio(r)].bg
const blockIcon = (r) => STATUS_COLORS[statusForRatio(r)].icon
const blockText = (r) => STATUS_COLORS[statusForRatio(r)].text
const blockSub = (r) => STATUS_COLORS[statusForRatio(r)].sub
const badgeClass = (r) => `${STATUS_COLORS[statusForRatio(r)].bg} ${STATUS_COLORS[statusForRatio(r)].text}`
</script>
